import React, { useMemo } from "react"
import { DateTime } from "luxon"
import { Clock, X } from "lucide-react"

function parseLogTime(isoLike) {
    if (!isoLike) {
        return null
    }
    const raw = String(isoLike)
    const iso = raw.endsWith("Z") ? raw : `${raw}Z`
    const dt = DateTime.fromISO(iso)
    return dt.isValid ? dt : null
}

function parseDateOnly(isoDate) {
    if (!isoDate) {
        return null
    }
    const dt = DateTime.fromISO(String(isoDate))
    return dt.isValid ? dt : null
}

function buildTimelineEvents(order, boxUpBoxes, boxTypes) {
    if (!order) {
        return []
    }

    /** @type {{time: import("luxon").DateTime, description: string, priority?: number}[]} */
    const events = []

    const createdAt = parseLogTime(order.created_at)
    if (createdAt) {
        events.push({ time: createdAt, description: "Order created in OMS" })
    }

    const updatedAt = parseLogTime(order.updated_at)
    if (updatedAt) {
        events.push({
            time: updatedAt,
            description: "Order last updated",
            priority: 10,
        })
    }

    const orderedAt = parseDateOnly(order.ordered_at)
    if (orderedAt) {
        events.push({ time: orderedAt, description: "Customer order date" })
    }

    const confirmedAt = parseLogTime(order.confirmed_at)
    if (confirmedAt) {
        const initial = (order.staff_initial ?? "").trim() || "unknown"
        events.push({
            time: confirmedAt,
            description: `Order confirmed by ${initial}`,
        })
    }

    const shippedAt = parseLogTime(order.shipped_at)
    if (shippedAt) {
        events.push({
            time: shippedAt,
            description: "Order transferred from drop off location",
        })
    }

    const receivedAt = parseDateOnly(order.received_at)
    if (receivedAt) {
        events.push({
            time: receivedAt,
            description: "Order received from drop off location",
        })
    }

    const weighedInAt = parseLogTime(order.weighed_in_at)
    if (weighedInAt) {
        events.push({ time: weighedInAt, description: "Order weighed in" })
    }

    const mmddnSyncedAt = parseLogTime(order.mmddn_synced_at)
    if (mmddnSyncedAt) {
        events.push({
            time: mmddnSyncedAt,
            description: "Disassembly production run created in Minotaur",
        })
    }

    const iopmSyncedAt = parseLogTime(order.iopm_synced_at)
    if (iopmSyncedAt) {
        events.push({
            time: iopmSyncedAt,
            description: "Sales order created in Minotaur",
            priority: 1,
        })
    }

    const confirmEmailAt = parseLogTime(order.confirm_email_at)
    if (confirmEmailAt) {
        events.push({
            time: confirmEmailAt,
            description: "Confirmation email last sent to customer",
        })
    }

    const confirmSmsAt = parseLogTime(order.confirm_sms_at)
    if (confirmSmsAt) {
        events.push({
            time: confirmSmsAt,
            description: "Confirmation SMS last sent to customer",
        })
    }

    const cnBoxSavedAt = parseLogTime(order.cn_box_saved_at)
    if (cnBoxSavedAt) {
        events.push({ time: cnBoxSavedAt, description: "Saved draft of canned box up" })
    }

    const fzBoxSavedAt = parseLogTime(order.fz_box_saved_at)
    if (fzBoxSavedAt) {
        events.push({ time: fzBoxSavedAt, description: "Saved draft of frozen box up" })
    }

    const cnVerifiedAt = parseLogTime(order.cn_verified_at)
    if (cnVerifiedAt) {
        events.push({
            time: cnVerifiedAt,
            description: "Canned box up verified in Minotaur",
        })
    }

    const fzVerifiedAt = parseLogTime(order.fz_verified_at)
    if (fzVerifiedAt) {
        events.push({
            time: fzVerifiedAt,
            description: "Frozen box up verified in Minotaur",
        })
    }

    ; (Array.isArray(order.shipments) ? order.shipments : []).forEach((shipment) => {
        const typeLabel = shipment?.type === "canned" ? "Canned"
            : shipment?.type === "frozen" ? "Frozen"
                : shipment?.type === "both" ? "Depot"
                    : "Shipment"

        // Exclude shipment.created_at because its mostly noise
        // const createdAt = parseLogTime(shipment?.created_at)
        // if (createdAt) {
        //     events.push({ time: createdAt, description: `${typeLabel} shipment created` })
        // }

        const minoShipmentAt = parseLogTime(shipment?.mino_shipment_at)
        if (minoShipmentAt) {
            events.push({
                time: minoShipmentAt,
                description: `${typeLabel} shipment created in Minotaur`,
            })
        }

        const sendCheckoutAt = parseLogTime(shipment?.send_checkout_at)
        if (sendCheckoutAt) {
            events.push({
                time: sendCheckoutAt,
                description: `${typeLabel} checkout last posted to WooCommerce`,
            })
        }

        const markedPaidAt = parseLogTime(shipment?.marked_paid_at)
        if (markedPaidAt) {
            events.push({
                time: markedPaidAt,
                description: `${typeLabel} shipment marked paid in OMS`,
            })
        }

        const minoInvoiceAt = parseLogTime(shipment?.mino_invoice_at)
        if (minoInvoiceAt) {
            events.push({
                time: minoInvoiceAt,
                description: `${typeLabel} invoice created in Minotaur`,
            })
        }

        const twoShipAt = parseLogTime(shipment?.two_ship_at)
        if (twoShipAt) {
            events.push({
                time: twoShipAt,
                description: `${typeLabel} shipment synced to 2Ship`,
            })
        }
    })

    ; (Array.isArray(boxUpBoxes) ? boxUpBoxes : []).forEach((box, index) => {
        const boxTypeId = box?.boxTypeId ?? box?.box_type_id ?? null
        const boxType = (Array.isArray(boxTypes) ? boxTypes : []).find(
            (x) => x?.id === boxTypeId
        )
        const typeLabel = boxType?.type ? ` (${boxType.type})` : ""
        const frozen = Boolean(box?.frozen)
        const family = frozen ? "Frozen" : "Canned"

        const pickedAt = parseLogTime(box?.picked_at)
        if (pickedAt) {
            events.push({
                time: pickedAt,
                description: `${family} box ${index + 1}${typeLabel} picked`,
            })
        }

        const verifiedAt = parseLogTime(box?.verified_at)
        if (verifiedAt) {
            events.push({
                time: verifiedAt,
                description: `${family} box ${index + 1}${typeLabel} verified`,
            })
        }
    })

        ; (Array.isArray(order.events) ? order.events : []).forEach((event) => {
            const created = parseLogTime(event?.created_at)
            if (!created) {
                return
            }
            const type = event?.event_type
            if (type === "custom_email_sent") {
                const subject = String(event?.data?.subject ?? "").trim()
                const message = String(event?.data?.message ?? "").trim()
                const display = [subject, message].filter(Boolean).join(" - ")
                const clipped = display.length > 50 ? `${display.slice(0, 50)}...` : display
                events.push({
                    time: created,
                    description: `Email sent: "${clipped || "—"}"`,
                })
            }
            if (type === "custom_sms_sent") {
                const msg = String(event?.data?.message ?? "")
                const clipped = msg.length > 50 ? `${msg.slice(0, 50)}...` : msg
                events.push({
                    time: created,
                    description: `SMS sent: "${clipped || "—"}"`,
                })
            }
            if (type === "orders_merged") {
                const initial = String(event?.data?.staff_initial ?? "").trim() || "unknown"
                const deleted = (event?.data?.secondary_masters ?? [])
                    .map((m) => String(m ?? "").trim())
                    .filter(Boolean)
                    .map((m) => `#${m}`)
                const deletedLabel = deleted.length > 0
                    ? `: deleted ${deleted.join(", ")}`
                    : ""
                events.push({
                    time: created,
                    description: `Orders merged by ${initial}${deletedLabel}`,
                })
            }
        })

    return events
        .filter((e) => e?.time?.isValid && e.description)
        .sort((a, b) => {
            const diff = b.time.toMillis() - a.time.toMillis()
            if (diff) {
                return diff
            }
            return (b.priority ?? 0) - (a.priority ?? 0)
        })
}

/**
 * @param {{
 *   order: any,
 *   boxUpBoxes?: any[],
 *   boxTypes?: any[],
 *   isOpen: boolean,
 *   onClose: () => void,
 * }} props
 */
export default function OrderTimelineSlideout({
    order,
    boxUpBoxes = [],
    boxTypes = [],
    isOpen,
    onClose,
}) {
    const events = useMemo(
        () => buildTimelineEvents(order, boxUpBoxes, boxTypes),
        [order, boxUpBoxes, boxTypes]
    )

    return (
        <div
            className="position-fixed top-0 bottom-0 end-0 bg-white shadow-lg transition-transform"
            style={{
                width: "520px",
                maxWidth: "100%",
                transform: isOpen ? "translateX(0)" : "translateX(100%)",
                transitionDuration: "300ms",
                transitionTimingFunction: "ease-in-out",
                zIndex: 1040,
            }}
            aria-hidden={isOpen ? "false" : "true"}
        >
            <div className="position-relative d-flex h-100">
                <div className="w-100 d-flex flex-column">
                    <div
                        className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center text-white"
                        style={{ backgroundColor: "#0f6170" }}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <Clock size={18} />
                            <h2 className="fs-5 fw-semibold mb-0">Timeline</h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="btn btn-link text-white p-0 border-0"
                            style={{ opacity: 0.85 }}
                            type="button"
                        >
                            <X size={24} />
                        </button>
                    </div>

                    <div className="flex-grow-1 overflow-auto">
                        <div className="p-4">
                            {events.length === 0 ? (
                                <p className="mb-0 text-secondary">
                                    No timeline events available for this order.
                                </p>
                            ) : (
                                <ul className="list-unstyled mb-0">
                                    {events.map((event, index) => {
                                        const isLast = index === events.length - 1
                                        return (
                                            <li key={`${event.time.toMillis()}-${index}`}>
                                                <div className="pb-4">
                                                    <div className="d-flex gap-3">
                                                        <div
                                                            className="position-relative flex-shrink-0"
                                                            style={{ width: "22px", alignSelf: "stretch" }}
                                                        >
                                                            {!isLast ? (
                                                                <span
                                                                    className="position-absolute top-0 start-50 border-start"
                                                                    style={{
                                                                        top: "0px",
                                                                        bottom: "-25px",
                                                                        opacity: 1,
                                                                        transform: "translateX(-50%)",
                                                                        borderLeftColor:
                                                                            "rgba(15, 97, 112, 1)",
                                                                        borderLeftWidth: "2px",
                                                                        zIndex: 0,
                                                                    }}
                                                                    aria-hidden="true"
                                                                />
                                                            ) : null}
                                                            <div
                                                                className="rounded-circle position-relative"
                                                                style={{
                                                                    width: "22px",
                                                                    height: "22px",
                                                                    marginTop: "2px",
                                                                    backgroundColor: "#0f6170",
                                                                    boxShadow:
                                                                        "0 0 0 4px rgba(15, 97, 112, 0.12)",
                                                                    zIndex: 1,
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="flex-grow-1 d-flex justify-content-between gap-3">
                                                            <div>
                                                                <div className="text-dark">
                                                                    {event.description}
                                                                </div>
                                                                <div
                                                                    className="text-secondary"
                                                                    style={{ fontSize: "0.85rem" }}
                                                                >
                                                                    {event.time.toFormat(
                                                                        "yyyy-MM-dd HH:mm"
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </li>
                                        )
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

